import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams, useSearchParams, useBlocker } from "react-router";
import { Expander, Notice } from "../../../design/components";
import { ApiError } from "../../../api/client";
import { useEntity } from "../../../api/queries";
import { usePublishCrumbs } from "../../../app/crumbs";
import { FAMILIES, familyShort, type Family } from "../../../domain/families";
import { CoverageMeter } from "./CoverageMeter";
import { Group } from "./Group";
import { MappingTabs, filterGroups, readTab } from "./MappingTabs";
import { FamilyPicker, ProfileHeader } from "./ProfileHeader";
import { enabledFamiliesOf, noProfileCopy } from "./profileState";
import { StickySaveBar } from "./StickySaveBar";
import { createPreviewScheduler } from "./preview";
import { useMappingDraft } from "./useMappingDraft";
import styles from "./mapping.module.css";
import {
  applyDraft,
  gapsOf,
  type Blocker,
  type CoverageDto,
  type RowMeta,
  type StandardFieldDto,
  type StateBody,
  type ValidationBody,
} from "./types";
import {
  publishVersion,
  saveRow,
  startDraftFrom,
  startProfile,
  sendPreview,
  useEntityProfiles,
  useProfileVersion,
  useStandardFields,
  useTransforms,
  validateVersion,
  versionKey,
} from "./useProfileVersion";

/** Row captions, UBL paths and code lists come from the field catalogue; notes from the version. */
function metaOf(catalogue: StandardFieldDto[] | undefined, state: StateBody | undefined) {
  const meta: Record<string, RowMeta> = {};
  for (const field of catalogue ?? []) {
    meta[field.ota] = {
      label: field.title,
      ibt: field.ibt,
      level: field.level,
      ublPath: field.ublPath,
      codeList: field.codeList,
    };
  }
  for (const warning of state?.warnings ?? []) {
    meta[warning.key] = { ...meta[warning.key], note: warning.message };
  }
  return meta;
}

function BlockerList({ blockers, meta }: { blockers: Blocker[]; meta: Record<string, RowMeta> }) {
  return (
    <ul className={styles.blockerList}>
      {blockers.map((blocker) => (
        <li key={blocker.key}>
          <a href={`#row-${blocker.key}`}>{meta[blocker.key]?.label ?? blocker.key}</a> — {blocker.message}
        </li>
      ))}
    </ul>
  );
}

export function MappingStudio() {
  const { entityId, family: familyParam, version } = useParams();
  // The route carries the family (R7); anything but sales/selfbilled/rcm is not a page.
  const family = FAMILIES.some((spec) => spec.key === familyParam) ? (familyParam as Family) : undefined;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // The success notice belongs to the version the operator lands on, so it rides the navigation.
  const published = (location.state as { published?: string } | null)?.published ?? "";

  // Only an in-app path may be returned to; an absolute URL would be an open redirect.
  const returnToRaw = searchParams.get("returnTo") ?? "";
  const returnTo = returnToRaw.startsWith("/") && !returnToRaw.startsWith("//") ? returnToRaw : "";
  const keepReturn = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : "";

  const entity = useEntity(entityId);
  const profiles = useEntityProfiles(family ? entityId : undefined);
  const profile = family ? profiles.data?.find((item) => item.family === family) : undefined;
  const state = useProfileVersion(profile?.id, version);
  // The picker always lists the family in the URL, so a link to a family the company has not enabled
  // still shows where the operator is and lets them move on.
  const families = useMemo(() => {
    const enabled = enabledFamiliesOf(entity.data?.enabledDocTypes);
    return family && !enabled.includes(family) ? [...enabled, family] : enabled;
  }, [entity.data, family]);
  const catalogue = useStandardFields(state.data?.docType);
  const transforms = useTransforms();

  const dirtyRows = useMappingDraft((draft) => draft.dirtyRows);
  const failures = useMappingDraft((draft) => draft.failures);
  const setRow = useMappingDraft((draft) => draft.setRow);
  const discard = useMappingDraft((draft) => draft.discard);
  const dirtyCount = Object.keys(dirtyRows).length;

  const [busy, setBusy] = useState(false);
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [noSample, setNoSample] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [sampleText, setSampleText] = useState("");
  const [sampleError, setSampleError] = useState("");
  const [confirm, setConfirm] = useState<ValidationBody | null>(null);
  const [failure, setFailure] = useState("");

  // Broadcast to every Group; seq forces a re-broadcast on repeat clicks.
  const [expand, setExpand] = useState({ open: true, seq: 0 });
  const toggleAll = () => setExpand((prev) => ({ open: !prev.open, seq: prev.seq + 1 }));

  const versionState = profile?.versions.find((item) => String(item.version) === version)?.state;
  const readonly = (state.data?.readonly ?? false) || versionState === "published";

  usePublishCrumbs(
    entity.data && family ? ["Companies", entity.data.legalName, "Mapping", familyShort(family)] : null,
  );

  const meta = useMemo(() => metaOf(catalogue.data, state.data), [catalogue.data, state.data]);

  const sample = useMemo(() => {
    if (!sampleText.trim()) return undefined;
    try {
      return JSON.parse(sampleText) as unknown;
    } catch {
      return undefined;
    }
  }, [sampleText]);

  const profileId = profile?.id;
  const scheduler = useMemo(() => {
    if (!profileId || !version) return null;
    return createPreviewScheduler((body, signal) => sendPreview(profileId, version, body, signal), {
      onResult: (body) => {
        setPreviews(body.previews);
        setNoSample(false);
        setPreviewError("");
      },
      onError: (error) => {
        if (error instanceof ApiError && error.code === "NO_SAMPLE") {
          setNoSample(true);
          return;
        }
        setPreviewError(error instanceof Error ? error.message : String(error));
      },
    });
  }, [profileId, version]);

  useEffect(() => () => scheduler?.cancel(), [scheduler]);

  // The draft store outlives the route, so a draft never follows the operator to another version.
  useEffect(() => {
    setPreviewError("");
    return () => useMappingDraft.getState().discard();
  }, [profileId, version]);

  useEffect(() => {
    if (!scheduler || !state.data) return;
    scheduler.schedule(sample === undefined ? { overrides: dirtyRows } : { sample, overrides: dirtyRows });
  }, [scheduler, state.data, sample, dirtyRows]);

  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }: { currentLocation: { pathname: string }; nextLocation: { pathname: string } }) =>
        dirtyCount > 0 && currentLocation.pathname !== nextLocation.pathname,
      [dirtyCount],
    ),
  );

  const groups = useMemo(
    () => filterGroups(applyDraft(state.data?.groups ?? [], dirtyRows), readTab(searchParams.get("tab"))),
    [state.data, dirtyRows, searchParams],
  );

  const coverage: CoverageDto = useMemo(() => {
    const rows = gapsOf(applyDraft(state.data?.groups ?? [], dirtyRows));
    return {
      resolved: state.data?.coverage.resolved ?? 0,
      total: state.data?.coverage.total ?? 0,
      gaps: rows.map((row) => row.ota),
      gapLabels: Object.fromEntries(
        rows.map((row) => [row.ota, { label: meta[row.ota]?.label ?? row.title, ibt: meta[row.ota]?.ibt }]),
      ),
    };
  }, [state.data, dirtyRows, meta]);

  async function saveDraft() {
    if (!profileId || !version) return;
    setBusy(true);
    setFailure("");
    for (const [ota, entry] of Object.entries(useMappingDraft.getState().dirtyRows)) {
      if (!entry) {
        useMappingDraft.getState().clearRow(ota);
        continue;
      }
      try {
        await saveRow(profileId, version, ota, entry);
        // An edit landing mid-flush leaves a different entry behind; keep it dirty.
        const draft = useMappingDraft.getState();
        if (draft.dirtyRows[ota] === entry) draft.clearRow(ota);
      } catch (error) {
        useMappingDraft.getState().fail(ota, error instanceof Error ? error.message : String(error));
      }
    }
    await queryClient.invalidateQueries({ queryKey: versionKey(profileId, version) });
    setBusy(false);
  }

  async function openPublish() {
    if (!profileId || !version) return;
    setFailure("");
    setBusy(true);
    try {
      setConfirm(await validateVersion(profileId, version));
    } catch (error) {
      setFailure(error instanceof Error ? error.message : String(error));
    }
    setBusy(false);
  }

  async function doPublish() {
    if (!profileId || !version) return;
    setBusy(true);
    try {
      const summary = await publishVersion(profileId, version);
      setConfirm(null);
      discard();
      await queryClient.invalidateQueries({ queryKey: ["entities", entityId, "profiles"] });
      navigate(`/mapping/${entityId}/${family}/${summary.latestVersion}${keepReturn}`, {
        state: { published: `Version ${version} is published.` },
      });
    } catch (error) {
      if (error instanceof ApiError && error.code === "NOT_EXPORT_READY") {
        setFailure("This version is not ready to export, so nothing was published.");
      } else {
        setFailure(error instanceof Error ? error.message : String(error));
      }
    }
    setBusy(false);
  }

  async function newDraft() {
    const fromVersionId = profile?.versions.find((item) => String(item.version) === version)?.id;
    if (!state.data || !family || !fromVersionId) return;
    setBusy(true);
    try {
      const summary = await startDraftFrom(state.data.profile.entityId, family, fromVersionId);
      await queryClient.invalidateQueries({ queryKey: ["entities", entityId, "profiles"] });
      navigate(`/mapping/${entityId}/${family}/${summary.latestVersion}${keepReturn}`);
    } catch (error) {
      setFailure(error instanceof Error ? error.message : String(error));
    }
    setBusy(false);
  }

  /** The Studio's empty state: seed this family's first draft, then open it. */
  async function createProfile() {
    if (!entityId || !family) return;
    setBusy(true);
    setFailure("");
    try {
      const summary = await startProfile(entityId, family);
      await queryClient.invalidateQueries({ queryKey: ["entities", entityId, "profiles"] });
      navigate(`/mapping/${entityId}/${family}/${summary.latestVersion}${keepReturn}`, { replace: true });
    } catch (error) {
      if (error instanceof ApiError && error.code === "PROFILE_EXISTS") {
        // Made elsewhere in the meantime: the refreshed list opens it.
        await queryClient.invalidateQueries({ queryKey: ["entities", entityId, "profiles"] });
      } else {
        setFailure(error instanceof Error ? error.message : String(error));
      }
    }
    setBusy(false);
  }

  /** A family with a profile opens at its latest version; one without opens its empty state. */
  function selectFamily(next: Family) {
    const target = profiles.data?.find((item) => item.family === next);
    navigate(`/mapping/${entityId}/${next}/${target?.latestVersion ?? 1}${keepReturn}`);
  }

  function download() {
    if (!state.data) return;
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(state.data, null, 2)], { type: "application/json" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `mapping-${entityId}-${family}-v${version}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  const head = (
    <div className="page-head">
      <div className="grow">
        <h1 className="display">Mapping Studio</h1>
        <p className={styles.note}>
          Every field the gateway needs, for this company and this version of its mapping.
        </p>
      </div>
      {returnTo ? (
        <div className="page-actions">
          <button type="button" className="btn" onClick={() => navigate(returnTo)}>
            Back to onboarding
          </button>
        </div>
      ) : null}
    </div>
  );

  if (!family) {
    return (
      <>
        {head}
        <Notice kind="fail">This document family does not exist.</Notice>
      </>
    );
  }
  if (profiles.isPending || (profile && state.isPending)) return <>{head}</>;
  if (!profile) {
    return (
      <>
        {head}
        <div className={styles.profileHead}>
          <span className={styles.familyChip} aria-label="Document family">
            {familyShort(family)}
          </span>
          <div className={styles.profileActions}>
            <FamilyPicker family={family} families={families} onSelect={selectFamily} />
          </div>
        </div>
        <Notice kind="fail">{noProfileCopy(family)}</Notice>
        {failure && <Notice kind="fail">{failure}</Notice>}
        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void createProfile()}>
          Create profile from the ERPNext pre-seed
        </button>
      </>
    );
  }
  if (state.isError || !state.data) {
    return (
      <>
        {head}
        <Notice kind="fail">This company has no mapping version {version}.</Notice>
      </>
    );
  }

  return (
    <>
      {head}
      <ProfileHeader
        entity={entity.data}
        profile={profile}
        family={family}
        families={families}
        version={version ?? ""}
        coverage={state.data.coverage}
        readonly={readonly}
        dirtyCount={dirtyCount}
        busy={busy}
        onSelectFamily={selectFamily}
        onSelectVersion={(next) => navigate(`/mapping/${entityId}/${family}/${next}${keepReturn}`)}
        onSave={() => void saveDraft()}
        onPublish={() => void openPublish()}
        onDownload={download}
        onNewDraft={() => void newDraft()}
      />

      {published && <Notice kind="ok">{published}</Notice>}
      {failure && <Notice kind="fail">{failure}</Notice>}
      {previewError && <Notice kind="fail">The preview could not be built: {previewError}</Notice>}
      {noSample && (
        <Notice kind="info">
          No sample document has reached the hub for this company yet, so the preview column is empty. Paste
          one below to see values.
        </Notice>
      )}

      {confirm && (
        <div className={styles.publishPanel} role="dialog" aria-label="Publish new version">
          <strong>
            {state.data.coverage.resolved} of {state.data.coverage.total} required fields mapped
          </strong>
          {confirm.exportReady ? (
            <p className={styles.note}>This version is ready to export.</p>
          ) : (
            <>
              <p className={styles.note}>Fix these before publishing:</p>
              <BlockerList blockers={confirm.blockers} meta={meta} />
            </>
          )}
          <div className={styles.publishActions}>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setConfirm(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={!confirm.exportReady || busy}
              onClick={() => void doPublish()}
            >
              Publish
            </button>
          </div>
        </div>
      )}

      {blocker.state === "blocked" && (
        <div className={styles.publishPanel} role="dialog" aria-label="Unsaved changes">
          <strong>Leave with unsaved changes?</strong>
          <div className={styles.publishActions}>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => blocker.reset?.()}>
              Stay
            </button>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => {
                discard();
                blocker.proceed?.();
              }}
            >
              Discard and leave
            </button>
          </div>
        </div>
      )}

      <CoverageMeter coverage={coverage} allOpen={expand.open} onToggleAll={toggleAll} />
      <MappingTabs
        active={readTab(searchParams.get("tab"))}
        onSelect={(tab) => {
          const next = new URLSearchParams(searchParams);
          next.set("tab", tab);
          setSearchParams(next);
        }}
      />

      {groups.map((group) => (
        <Group
          key={group.key}
          group={group}
          entityId={entityId}
          meta={meta}
          transforms={transforms.data}
          previews={previews}
          failures={failures}
          readonly={readonly}
          expand={expand}
          onChange={setRow}
        />
      ))}

      <Expander
        head="Preview sample"
        body={
          <>
            <textarea
              aria-label="Sample document JSON"
              className={styles.sampleBox}
              value={sampleText}
              placeholder='{"doctype": "Sales Invoice", "name": "ACC-SINV-2026-00187"}'
              onChange={(event) => {
                setSampleText(event.target.value);
                try {
                  if (event.target.value.trim()) JSON.parse(event.target.value);
                  setSampleError("");
                } catch {
                  setSampleError("That is not valid JSON yet.");
                }
              }}
            />
            {sampleError && <p className={styles.error}>{sampleError}</p>}
          </>
        }
      />

      {!readonly && <StickySaveBar busy={busy} onSave={() => void saveDraft()} />}
    </>
  );
}
